import { Calculator } from 'lucide-react';
import { useState } from 'react';

import { Button } from '@/components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogTrigger,
} from '@/components/ui/dialog';
import type { ItemUnderRequest } from '@/pages/PurchasesContainer/PurchasesRequests/api/Request.interface.ts';
import { calculateItemPricing } from '@/Shared/RequestDialog/utils/priceCalculations';

interface SimpleCalculationsButtonProps {
  item: ItemUnderRequest;
}

interface CalculationItem {
  label: string;
  value: number;
  highlight?: 'primary' | 'success' | 'warning' | 'danger' | 'info' | 'none';
  group: 'pricing' | 'discount' | 'bonus' | 'tax' | 'totals';
  description?: string;
}

export function SimpleCalculationsButton({
  item,
}: SimpleCalculationsButtonProps) {
  const [open, setOpen] = useState(false);

  // Convert ItemUnderRequest to the expected IRequestItem interface
  const requestItem = {
    _id: item._id,
    Price: item.Price || 0,
    Qty: item.Qty || 0,
    Discount: item.Discount || 0,
    DiscountPro: item.DiscountPro || 0,
    Bonus: item.Bonus || 0,
    Tax: item.Tax || 0,
    itemName: item.itemName || '',
    // Add other required fields with defaults
    Client: item.Client || { _id: '', ID: 0, suppliarClass: '' },
    User: item.User || { _id: '', empName: '', avatar: '' },
    ClientID: item.ClientID || 0,
    PurchaseRequestHead: item.PurchaseRequestHead || '',
    TrDate: item.TrDate || '',
    HealthyItemID: item.HealthyItemID || 0,
    MainID: item.MainID || null,
    BNO: item.BNO || null,
    FinishTax: 0,
    IsDelivered: false,
    IsCancel: false,
    RequestType: '',
    ExpireDate: item.ExpireDate || null,
    RetailStatus: item.RetailStatus || 'needStock',
    WholeSaleStatus: item.WholeSaleStatus || 'needStock',
    UnderRequest: item.UnderRequest || 0,
    itemSqlPrice: item.Price || 0,
    DiscountStatus: item.DiscountStatus || 'static',
  };

  // Calculate all pricing for the item using the shared utility
  const calculations = calculateItemPricing(requestItem as any);

  // Define calculation items to show in dropdown - same as CalculationsDropdown
  const calculationItems: CalculationItem[] = [
    {
      label: 'سعر الجمهور',
      value: item.Price || 0,
      highlight: 'none',
      group: 'pricing',
      description: 'سعر البيع الرسمي للجمهور',
    },
    {
      label: 'السعر الأساسي',
      value: calculations.mainPrice,
      highlight: 'primary',
      group: 'pricing',
      description: 'السعر بدون ضريبة',
    },
    {
      label: 'الكمية',
      value: calculations.qty,
      highlight: 'none',
      group: 'pricing',
    },
    {
      label: 'إجمالي الكمية مع البونص',
      value: calculations.totalQty,
      highlight: 'info',
      group: 'bonus',
    },
    {
      label: 'الخصم',
      value: item.Discount || 0,
      highlight: 'warning',
      group: 'discount',
      description: 'نسبة الخصم المتفق عليها',
    },
    {
      label: 'الخصم الصافي',
      value: calculations.netDiscount,
      highlight: 'warning',
      group: 'discount',
      description: 'نسبة الخصم النهائية بعد البونص',
    },
    {
      label: 'خصم الدفع المبكر',
      value: item.DiscountPro || 0,
      highlight: 'warning',
      group: 'discount',
      description: 'خصم اضافي عند السداد المبكر',
    },
    {
      label: 'البونص',
      value: item.Bonus || 0,
      highlight: 'success',
      group: 'bonus',
      description: 'عدد القطع المجانية',
    },
    {
      label: 'قيمة البونص',
      value: calculations.bonusValue,
      highlight: 'success',
      group: 'bonus',
      description: 'القيمة المالية للبونص',
    },
    {
      label: 'الضريبة',
      value: item.Tax || 0,
      highlight: 'danger',
      group: 'tax',
      description: 'قيمة الضريبة للقطعة الواحدة',
    },
    {
      label: 'إجمالي الضريبة',
      value: calculations.totalTax,
      highlight: 'danger',
      group: 'tax',
      description: 'اجمالي الضريبة على الصنف',
    },
    {
      label: 'إجمالي سعر الجمهور',
      value: calculations.totalCustomerPrice,
      highlight: 'none',
      group: 'totals',
      description: 'المجموع قبل الخصم والضريبة',
    },
    {
      label: 'إجمالي بعد الخصم',
      value: calculations.TotalAfterDiscount,
      highlight: 'primary',
      group: 'totals',
      description: 'المجموع النهائي بعد الخصم',
    },
    {
      label: 'متوسط السعر',
      value: calculations.avgPrice,
      highlight: 'info',
      group: 'totals',
      description: 'متوسط سعر القطعة مع البونص',
    },
  ];

  // Group items by their group property
  const groupedItems: Record<string, CalculationItem[]> = {
    pricing: [],
    discount: [],
    bonus: [],
    tax: [],
    totals: [],
  };

  // Group items
  calculationItems.forEach((item) => {
    if (groupedItems[item.group]) {
      groupedItems[item.group].push(item);
    }
  });

  // Get group labels
  const groupLabels: Record<string, string> = {
    pricing: 'الأسعار والكميات',
    discount: 'الخصومات',
    bonus: 'البونص',
    tax: 'الضريبة',
    totals: 'الإجماليات',
  };

  // Get highlight color class based on type
  const getHighlightClass = (highlight: CalculationItem['highlight']) => {
    switch (highlight) {
      case 'primary':
        return 'text-blue-600 dark:text-blue-400';
      case 'success':
        return 'text-green-600 dark:text-green-400';
      case 'warning':
        return 'text-amber-600 dark:text-amber-400';
      case 'danger':
        return 'text-red-600 dark:text-red-400';
      case 'info':
        return 'text-cyan-600 dark:text-cyan-400';
      default:
        return 'text-foreground';
    }
  };

  // Format number in English numerals
  const formatNumberEnglish = (value: number): string => {
    return new Intl.NumberFormat('en-US', {
      minimumFractionDigits: 0,
      maximumFractionDigits: 2,
    }).format(value);
  };

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>
        <Button
          variant="ghost"
          size="sm"
          className="flex h-7 w-7 items-center justify-center p-0 hover:bg-slate-100 dark:hover:bg-slate-800"
        >
          <Calculator className="h-3.5 w-3.5" />
        </Button>
      </DialogTrigger>
      <DialogContent
        size="md"
        onPointerDownOutside={(e) => e.preventDefault()}
        onInteractOutside={(e) => e.preventDefault()}
        aria-describedby="dialog-description"
        className="max-w-[650px] p-0 md:max-w-[650px]"
      >
        <DialogHeader className="-mx-0 -mt-0 mb-1 rounded-t-lg border-b border-primary/20 bg-primary/10 px-3 py-2">
          <DialogTitle className="text-center text-base font-bold text-primary">
            تفاصيل الحسابات المالية
          </DialogTitle>
          <DialogDescription
            id="dialog-description"
            className="mt-0.5 flex flex-col items-center gap-0.5 text-center"
          >
            هذه الشاشة تعرض جميع الحسابات التفصيلية للصنف، مجمعة حسب الفئات
          </DialogDescription>
        </DialogHeader>

        <div className="max-h-[60vh] overflow-y-auto p-2">
          <div className="grid grid-cols-1 gap-1.5 md:grid-cols-2">
            {Object.keys(groupedItems).map((groupKey, groupIndex) => {
              const groupItems = groupedItems[groupKey];
              if (groupItems.length === 0) return null;

              return (
                <div key={groupKey} className="col-span-full mt-1 first:mt-0">
                  {groupIndex > 0 && (
                    <div className="my-1.5 h-px bg-primary/10" />
                  )}
                  <div className="mb-1 rounded-md bg-primary/10 px-2 py-1 text-sm font-bold text-primary">
                    {groupLabels[groupKey]}
                  </div>
                  <div className="grid grid-cols-1 gap-1.5 md:grid-cols-2">
                    {groupItems.map((calcItem, index) => (
                      <div
                        key={index}
                        className="flex items-center justify-between rounded-md border border-border/50 px-2 py-1.5 transition-colors hover:bg-muted/30"
                      >
                        <span
                          className={`ltr font-mono text-base font-bold ${getHighlightClass(calcItem.highlight)}`}
                          dir="ltr"
                        >
                          {formatNumberEnglish(calcItem.value)}
                        </span>
                        <div className="flex flex-col items-end">
                          <span className="text-xs font-medium">
                            {calcItem.label}
                          </span>
                          {calcItem.description && (
                            <span className="text-[10px] text-muted-foreground">
                              {calcItem.description}
                            </span>
                          )}
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}
